import React, {
    useEffect,
    useMemo,
    useState
} from 'react';
import EStyleSheet from 'react-native-extended-stylesheet';
import { useSelector } from 'react-redux';

import { View } from '../../common';
import { ConsentCheckbox } from '../../common/ConsentCheckbox';
import {
    CHILD_CLINIC_HISTORY_KEY,
    CHILD_HISTORY_KEY,
    usePersonalData,
    USER_CLINIC_HISTORY_KEY,
    USER_HISTORY_KEY
} from '../../hooks/usePersonalData';
import { getProviderCodes } from '../../selectors/providerCodes';
import { isEmpty } from '../../utils';
import { isProviderHasCapability } from '../../utils/consentsUtils';

export const LoadVisitConsents = ({
    profileId,
    branchId,
    onComplete,
}) => {
    const {
        allConsents: consents,
    } = usePersonalData();

    const providerCodes = useSelector(getProviderCodes);

    const providerCode = useMemo(() => providerCodes?.clinics?.[branchId]?.providerCode, [ providerCodes, branchId ]);
    const haveCapability = useMemo(() => isProviderHasCapability({providerCode, capabilities: providerCodes?.providerCapabilities}), [providerCode, providerCodes?.providerCapabilities]);

    const [ currentHistoryConsent, setCurrentHistoryConsent ] = useState();
    const [ currentClinicHistoryConsent, setCurrentClinicHistoryConsent ] = useState();

    const isChild = useMemo(() => {
        if (profileId === consents?.profileId) {
            return false;
        } else if (consents?.family?.[profileId]) {
            return true;
        }
        return null;
    }, [ consents, profileId ]);

    const historyAgreementKey = useMemo(() => {
        if (isChild === false) {
            return USER_HISTORY_KEY;
        } else if (isChild === true) {
            return CHILD_HISTORY_KEY;
        }
        return null;
    }, [ isChild ]);

    const clinicHistoryAgreementKey = useMemo(() => {
        if (isChild === false) {
            return USER_CLINIC_HISTORY_KEY;
        } else if (isChild === true) {
            return CHILD_CLINIC_HISTORY_KEY;
        }
        return null;
    }, [ isChild ]);

    const historyConsent = useMemo(() => {
        if (isChild === true) {
            return consents?.family?.[profileId]?.main?.[CHILD_HISTORY_KEY];
        } else if (isChild === false) {
            return consents?.user?.main?.[USER_HISTORY_KEY];
        }
        return true;
    }, [ consents?.family,
        consents?.user?.main,
        isChild,
        profileId ]);

    const clinicHistoryConsent = useMemo(() => {
        if (!haveCapability) {
            return true;
        }
        if (isChild === true) {
            return consents?.family?.[profileId]?.[providerCode]?.[CHILD_CLINIC_HISTORY_KEY];
        } else if (isChild === false) {
            return consents?.user?.[providerCode]?.[USER_CLINIC_HISTORY_KEY];
        }
        return true;
    }, [ consents?.family,
        consents?.user,
        haveCapability,
        isChild,
        profileId,
        providerCode ]);


    const isConsentVisible = useMemo(() => {
        return !historyConsent;
    }, [ historyConsent ]);

    const isClinicConsentVisible = useMemo(() => {
        return !clinicHistoryConsent;
    }, [ clinicHistoryConsent ]);

    useEffect(() => {
        setCurrentHistoryConsent(false);
        setCurrentClinicHistoryConsent(false);
    }, [ profileId ]);

    useEffect(() => {
        const isDone = ((historyConsent || currentHistoryConsent) && (clinicHistoryConsent || currentClinicHistoryConsent)) || isChild === null;
        const data = {};
        if (!historyConsent) {
            data[historyAgreementKey] = currentHistoryConsent;
        }

        if (!clinicHistoryConsent) {
            data[clinicHistoryAgreementKey] = currentClinicHistoryConsent;
        }
        onComplete?.(isDone, isEmpty(data) ? null : data);
    }, [ historyConsent,
        clinicHistoryConsent,
        currentHistoryConsent,
        currentClinicHistoryConsent,
        onComplete,
        isChild,
        historyAgreementKey,
        clinicHistoryAgreementKey,
        providerCode ]);

    if (!branchId || !profileId || !providerCode) {
        onComplete?.(false, {});
        return null;
    }

    return ((isConsentVisible && historyAgreementKey) || isClinicConsentVisible && clinicHistoryAgreementKey) && <View style={[ styles.container ]}>
        {isClinicConsentVisible && clinicHistoryAgreementKey && <ConsentCheckbox
            profileId={profileId}
            providerCode={providerCode}
            agreementKey={clinicHistoryAgreementKey}
            onChange={setCurrentClinicHistoryConsent}
            containerStyle={{ paddingBottom: 12 }}
        />}
        {isConsentVisible && historyAgreementKey && <View style={[ styles.box ]}><ConsentCheckbox
            profileId={profileId}
            providerCode={providerCode}
            agreementKey={historyAgreementKey}
            onChange={setCurrentHistoryConsent}/></View>}
    </View>;
};

const styles = EStyleSheet.create({
    container: {
        padding: 12,
    },
    box: {
        paddingBottom: 12,
    },
});
